import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ForumTopicListResponse } from '../../services/forumService';

interface ForumDesktopProps {
    data: ForumTopicListResponse | null;
    loading: boolean;
    activeFilter: string;
    setActiveFilter: (filter: string) => void;
    handleDeleteTopic: (e: React.MouseEvent, topicId: number) => void;
    formatDate: (dateStr: string) => string;
    CATEGORY_CONFIG: any;
    user: any;
}

const ForumDesktop: React.FC<ForumDesktopProps> = ({
    data,
    loading,
    activeFilter,
    setActiveFilter,
    handleDeleteTopic,
    formatDate,
    CATEGORY_CONFIG,
    user
}) => {
    const navigate = useNavigate();
    const getCat = (category: string) => CATEGORY_CONFIG[category] || CATEGORY_CONFIG.GENERAL;
    const filteredTopics = data?.topics.filter(t => activeFilter === 'ALL' || t.category === activeFilter) || [];
    const filters = ['ALL', ...Object.keys(CATEGORY_CONFIG)];

    return (
        <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
            {/* Header Section */}
            <div style={{
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(168, 85, 247, 0.1) 50%, rgba(236, 72, 153, 0.08) 100%)',
                borderRadius: '16px',
                padding: '32px',
                marginBottom: '28px',
                border: '1px solid rgba(99, 102, 241, 0.15)',
                position: 'relative',
                overflow: 'hidden',
            }}>
                <div style={{
                    position: 'absolute', top: '-40px', right: '-40px', width: '180px', height: '180px',
                    background: 'radial-gradient(circle, rgba(139, 92, 246, 0.2) 0%, transparent 70%)',
                    borderRadius: '50%', pointerEvents: 'none',
                }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                    <div>
                        <h1 style={{
                            color: 'var(--text-main)', fontSize: '28px', margin: '0 0 8px 0',
                            fontWeight: 800, letterSpacing: '-0.5px',
                        }}>
                            💬 Discussions
                        </h1>
                        <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '15px' }}>
                            Échangez analyses, stratégies et convictions avec d'autres investisseurs BRVM.
                        </p>
                    </div>
                    <button
                        onClick={() => navigate('/forum/new')}
                        style={{
                            background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                            color: 'white',
                            border: 'none',
                            padding: '12px 24px',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            fontWeight: 700,
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            boxShadow: '0 4px 15px rgba(99, 102, 241, 0.35)',
                            transition: 'all 0.2s ease',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <span style={{ fontSize: '18px' }}>✏️</span> Nouveau Sujet
                    </button>
                </div>
            </div>

            {/* Filter Pills */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
                {filters.map(f => {
                    const isActive = activeFilter === f;
                    const cat = f === 'ALL' ? { label: 'Tous', color: '#94a3b8', bg: 'rgba(148, 163, 184, 0.1)' } : getCat(f);
                    return (
                        <button
                            key={f}
                            onClick={() => setActiveFilter(f)}
                            style={{
                                padding: '8px 16px',
                                borderRadius: '20px',
                                border: `1px solid ${isActive ? cat.color : 'transparent'}`,
                                background: isActive ? cat.bg : 'rgba(255,255,255,0.03)',
                                color: isActive ? cat.color : 'var(--text-muted)',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: isActive ? 700 : 500,
                                transition: 'all 0.2s ease',
                            }}
                        >
                            {cat.label}
                        </button>
                    );
                })}
            </div>

            {/* Stats Row */}
            {data && (
                <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
                    {[
                        { label: 'Sujets', value: data.total_count, icon: '📋', color: '#8b5cf6' },
                        { label: 'Réponses', value: data.topics.reduce((s, t) => s + t.replies_count, 0), icon: '💭', color: '#06b6d4' },
                        { label: 'Vues totales', value: data.topics.reduce((s, t) => s + t.views_count, 0), icon: '👁️', color: '#f59e0b' },
                    ].map((stat, i) => (
                        <div key={i} style={{
                            flex: 1, minWidth: '150px',
                            background: 'rgba(255,255,255,0.03)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '12px',
                            padding: '16px 20px',
                            display: 'flex', alignItems: 'center', gap: '14px',
                        }}>
                            <span style={{ fontSize: '24px' }}>{stat.icon}</span>
                            <div>
                                <div style={{ color: stat.color, fontSize: '22px', fontWeight: 800 }}>{stat.value}</div>
                                <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{stat.label}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Topics List */}
            {loading ? (
                <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text-muted)' }}>
                    <div style={{ fontSize: '32px', marginBottom: '12px' }}>💬</div>
                    Chargement des discussions...
                </div>
            ) : filteredTopics.length === 0 ? (
                <div style={{
                    padding: '60px', textAlign: 'center',
                    background: 'rgba(255,255,255,0.02)',
                    borderRadius: '16px',
                    border: '1px dashed var(--border-color)',
                }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🗣️</div>
                    <div style={{ color: 'var(--text-main)', fontWeight: 700, fontSize: '18px', marginBottom: '8px' }}>Aucune discussion</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Soyez le premier à lancer un sujet !</div>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {filteredTopics.map((topic) => {
                        const cat = getCat(topic.category);
                        return (
                            <div
                                key={topic.id}
                                onClick={() => navigate(`/forum/topic/${topic.id}`)}
                                style={{
                                    background: 'rgba(255,255,255,0.02)',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '14px',
                                    padding: '20px 24px',
                                    cursor: 'pointer',
                                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '18px',
                                    position: 'relative',
                                    overflow: 'hidden',
                                }}
                            >
                                {/* Left accent bar */}
                                <div style={{
                                    position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px',
                                    background: `linear-gradient(to bottom, ${cat.color}, transparent)`,
                                    borderRadius: '4px 0 0 4px',
                                }} />

                                {/* Avatar */}
                                <div style={{
                                    width: '48px', height: '48px', borderRadius: '14px', flexShrink: 0,
                                    background: `linear-gradient(135deg, ${cat.color}33, ${cat.color}11)`,
                                    border: `1px solid ${cat.color}44`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '20px',
                                }}>
                                    {cat.icon}
                                </div>

                                {/* Content */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
                                        <span style={{
                                            color: 'var(--text-main)', fontWeight: 700, fontSize: '15px',
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                        }}>
                                            {topic.title}
                                        </span>
                                        {topic.stock_symbol && (
                                            <span style={{
                                                backgroundColor: 'rgba(56, 189, 248, 0.15)',
                                                color: '#38bdf8',
                                                padding: '2px 8px',
                                                borderRadius: '6px',
                                                fontSize: '11px',
                                                fontWeight: 700,
                                                letterSpacing: '0.5px',
                                            }}>
                                                ${topic.stock_symbol}
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '13px', color: 'var(--text-muted)' }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <span style={{
                                                width: '20px', height: '20px', borderRadius: '6px',
                                                background: `linear-gradient(135deg, ${cat.color}, ${cat.color}88)`,
                                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                color: 'white', fontWeight: 700, fontSize: '11px',
                                            }}>
                                                {topic.author_name.charAt(0).toUpperCase()}
                                            </span>
                                            {topic.author_name}
                                        </span>
                                        <span style={{
                                            padding: '2px 8px', borderRadius: '6px',
                                            background: cat.bg, color: cat.color,
                                            fontSize: '11px', fontWeight: 600,
                                        }}>
                                            {cat.label}
                                        </span>
                                    </div>
                                </div>

                                {/* Stats */}
                                <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexShrink: 0 }}>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ color: topic.replies_count > 0 ? '#06b6d4' : 'var(--text-muted)', fontWeight: 800, fontSize: '16px' }}>
                                            {topic.replies_count}
                                        </div>
                                        <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>réponses</div>
                                    </div>
                                    <div style={{ textAlign: 'right', minWidth: '80px' }}>
                                        <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                                            {formatDate(topic.last_activity_at)}
                                        </div>
                                    </div>
                                    {user?.id === topic.author_id && (
                                        <button
                                            onClick={(e) => handleDeleteTopic(e, topic.id)}
                                            style={{
                                                background: 'transparent', border: 'none', color: 'var(--text-muted)',
                                                cursor: 'pointer', fontSize: '14px', padding: '8px', borderRadius: '50%',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                transition: 'all 0.2s ease', opacity: 0.6,
                                            }}
                                            title="Supprimer ce sujet"
                                        >
                                            🗑️
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ForumDesktop;
